// An icon on a slide (S7): one of Lucide's, drawn from the board's own set (icons/set.json, loaded once, only when a
// slide shows an icon or the picker opens) in the box's colour and stroke, as large as its box. The drawings are plain
// shapes and numbers; nothing in them runs. Also drawn to a picture for the PowerPoint file.

import { createElement, useEffect, useState, type ReactNode } from "react";

export type IconNode = [string, Record<string, string | number>][];
export interface IconSet {
  v: string;
  icons: Record<string, IconNode>;
}

let loaded: IconSet | null = null;
let loading: Promise<IconSet> | null = null;

/** The board's icons, loaded once. */
export function loadIcons(): Promise<IconSet> {
  if (loaded) return Promise.resolve(loaded);
  // the set as a file of its own, fetched once (and cached by the browser), never part of the board's code
  loading ??= import("./set.json?url")
    .then((m) => fetch(m.default))
    .then((r) => r.json() as Promise<IconSet>)
    .then((s) => (loaded = s))
    .catch((e) => {
      loading = null;
      throw e;
    });
  return loading;
}

/** The icon set, once it is in (and a redraw when it comes). */
export function useIcons(): IconSet | null {
  const [set, setSet] = useState<IconSet | null>(loaded);
  useEffect(() => {
    if (set) return;
    let live = true;
    void loadIcons().then((s) => live && setSet(s));
    return () => {
      live = false;
    };
  }, [set]);
  return set;
}

const TAGS = new Set(["path", "circle", "rect", "line", "polyline", "polygon", "ellipse"]);
const ATTRS = new Set(["d", "cx", "cy", "r", "x", "y", "width", "height", "rx", "ry", "points", "x1", "y1", "x2", "y2"]);

/** An icon's shapes as SVG elements: only the shapes and numbers an icon has. */
export function iconShapes(node: IconNode | undefined): ReactNode[] {
  return (node ?? []).filter(([tag]) => TAGS.has(tag)).map(([tag, attrs], i) => createElement(tag, { key: i, ...Object.fromEntries(Object.entries(attrs).filter(([k]) => ATTRS.has(k))) }));
}

/** An icon drawn in a colour and a stroke: an SVG of 24 by 24 that fills its place. */
export function IconSvg({ name, color, stroke = 2, size, className }: { name: string; color: string; stroke?: number; size?: number; className?: string }) {
  const set = useIcons();
  const node = set?.icons[name];
  return (
    <svg className={className} viewBox="0 0 24 24" width={size ?? "100%"} height={size ?? "100%"} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" preserveAspectRatio="xMidYMid meet" aria-hidden>
      {node ? iconShapes(node) : set ? <circle cx="12" cy="12" r="9" strokeDasharray="2 3" opacity={0.5} /> : null}
    </svg>
  );
}

const esc = (v: string | number) => String(v).replace(/[^0-9A-Za-z .,\-#]/g, "");

/** An icon as a PNG picture (for the PowerPoint file), drawn at four times its size in the slide's pixels. */
export async function iconPicture(name: string, color: string, stroke: number, w: number, h: number): Promise<string | null> {
  if (typeof document === "undefined") return null;
  const set = await loadIcons();
  const node = set.icons[name];
  if (!node) return null;
  const side = Math.max(16, Math.min(1600, Math.round(Math.min(w, h) * 4)));
  const shapes = node
    .filter(([tag]) => TAGS.has(tag))
    .map(([tag, attrs]) => `<${tag} ${Object.entries(attrs).filter(([k]) => ATTRS.has(k)).map(([k, v]) => `${k}="${esc(v)}"`).join(" ")}/>`)
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${side}" height="${side}" fill="none" stroke="${esc(color)}" stroke-width="${esc(stroke)}" stroke-linecap="round" stroke-linejoin="round">${shapes}</svg>`;
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await img.decode();
  const c = document.createElement("canvas");
  c.width = side;
  c.height = side;
  c.getContext("2d")?.drawImage(img, 0, 0, side, side);
  return c.toDataURL("image/png");
}
